/** UXP-00007 — Catalog Operations Platform framework (mirrors pkg/cataloglifecycle) */

export const UXP_CO_ID = 'UXP-00007';

export interface ReleasePackage {
  code: string;
  status: string;
  items: number;
  marketplaces: string[];
  scheduled: string;
}

export interface SimulationScore {
  label: string;
  score: number;
  risk: 'low' | 'medium' | 'high';
}

export interface PublishJob {
  id: string;
  release: string;
  state: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  detail: string;
}

export interface SuppressionItem {
  asin: string;
  title: string;
  reason: string;
  fix: string;
  severity: 'high' | 'medium' | 'low';
}

export interface ReviewItem {
  id: string;
  type: string;
  title: string;
  status: string;
  assignee: string;
}

export const EXPLORER_FEATURES = [
  'Product Explorer', 'Collections', 'Smart Collections', 'Marketplace Groups',
  'Brand Groups', 'Category Groups', 'Saved Views', 'Favorites', 'Pinned Objects',
];

export const LIFECYCLE_STAGES = [
  'Idea', 'Research', 'Draft', 'AI Generated', 'Review', 'Approval',
  'Scheduling', 'Publishing', 'Monitoring', 'Optimization', 'Archival',
];

export const RELEASE_CONTENTS = [
  'Listings', 'Images', 'Videos', 'A+', 'Brand Story', 'SEO',
  'Attributes', 'Pricing', 'Marketplace Metadata', 'Approval Records', 'Rollback Metadata',
];

export const VERSION_FEATURES = [
  'Version History', 'Compare Versions', 'Diff Viewer', 'Restore', 'Rollback',
  'AI History', 'Human History', 'Approval History',
];

export const DRAFT_CAPABILITIES = [
  'Multiple Drafts', 'Team Drafts', 'Marketplace Drafts', 'AI Drafts', 'Branch-style Editing',
];

export const REVIEW_TYPES = [
  'Content Review', 'Creative Review', 'SEO Review', 'Compliance Review',
  'Marketplace Review', 'AI-assisted Review', 'Multi-level Approvals', 'Conditional Approvals',
];

export const SIMULATION_METRICS = [
  'SEO Score', 'CTR', 'Conversion', 'Compliance', 'Brand Alignment',
  'Marketplace Readiness', 'Revenue Impact', 'Risk', 'Confidence',
];

export const VALIDATION_CHECKS = [
  'Category Requirements', 'Variation Rules', 'Image Compliance', 'Video Compliance',
  'Text Compliance', 'Search Terms', 'A+', 'Brand Registry',
];

export const ADVISOR_RECOMMENDATIONS = [
  'Publish', 'Delay', 'Improve Content', 'Improve Creative', 'Improve SEO',
  'Improve Inventory', 'Launch Advertising', 'Best Publishing Time',
];

export const SCHEDULING_MODES = [
  'Immediate Publishing', 'Scheduled Publishing', 'Marketplace-specific Publishing',
  'Campaign Synchronization', 'Timezone Awareness',
];

export const ROLLBACK_SCOPES = [
  'Full Rollback', 'Partial Rollback', 'Marketplace Rollback', 'Asset Rollback', 'Release Rollback',
];

export const PUBLISH_JOB_STATES = [
  'Queued Jobs', 'Running Jobs', 'Completed Jobs', 'Failed Jobs', 'Retries', 'Marketplace Responses',
];

export const SUPPRESSION_FEATURES = [
  'Suppressed Listings', 'Marketplace Errors', 'Compliance Violations',
  'AI Root Cause Analysis', 'Recommended Fixes', 'Republishing',
];

export const BULK_OPERATIONS = [
  'Bulk Publish', 'Bulk Approval', 'Bulk Rollback',
  'Bulk AI Optimization', 'Bulk Scheduling', 'Bulk Validation',
];

export const COLLABORATION_FEATURES = [
  'Comments', 'Mentions', 'Assignments', 'Approvals', 'Annotations', 'Decision History', 'Audit',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Catalog Explorer', 'Product Lifecycle', 'Release Packages', 'Version Control',
  'Review & Approval', 'Simulation Engine', 'Marketplace Validation',
  'Publishing Advisor', 'Scheduling & Monitor', 'Rollback & Suppression',
];

export const ANALYTICS_METRICS = [
  'Publishing Success', 'Marketplace Acceptance', 'Approval Time',
  'Rollback Frequency', 'Release Quality', 'Business Impact',
];

export const LIVE_ROUTES: Record<string, string> = {
  catalogOps: '/amazon/catalog-ops',
  publishing: '/publishing',
  catalog: '/amazon/catalog',
};

export const DEMO_RELEASES: ReleasePackage[] = [
  { code: 'REL-2026-000142', status: 'approved', items: 847, marketplaces: ['Amazon.in', 'Amazon.com'], scheduled: '2026-08-02 06:00 IST' },
  { code: 'REL-2026-000141', status: 'publishing', items: 23, marketplaces: ['Amazon.in'], scheduled: 'Immediate' },
  { code: 'REL-2026-000140', status: 'completed', items: 156, marketplaces: ['Amazon.in', 'Amazon.co.uk'], scheduled: '2026-07-30' },
];

export const DEMO_SIMULATION: SimulationScore[] = [
  { label: 'SEO Score', score: 84, risk: 'low' },
  { label: 'CTR', score: 76, risk: 'low' },
  { label: 'Conversion', score: 72, risk: 'medium' },
  { label: 'Compliance', score: 96, risk: 'low' },
  { label: 'Brand Alignment', score: 91, risk: 'low' },
  { label: 'Marketplace Readiness', score: 88, risk: 'low' },
  { label: 'Revenue Impact', score: 79, risk: 'medium' },
  { label: 'Risk', score: 18, risk: 'low' },
  { label: 'Confidence', score: 87, risk: 'low' },
];

export const DEMO_PUBLISH_JOBS: PublishJob[] = [
  { id: 'job-1', release: 'REL-2026-000141', state: 'running', progress: 67, detail: 'Publishing 23 listings to Amazon.in' },
  { id: 'job-2', release: 'REL-2026-000139', state: 'queued', progress: 0, detail: '847 items queued for Amazon.com' },
  { id: 'job-3', release: 'REL-2026-000138', state: 'failed', progress: 45, detail: '3 SKU attribute validation failures' },
  { id: 'job-4', release: 'REL-2026-000137', state: 'completed', progress: 100, detail: '156 items published successfully' },
];

export const DEMO_SUPPRESSIONS: SuppressionItem[] = [
  { asin: 'B0CX3456GH', title: 'Smart Home Hub Gen 3', reason: 'Missing required energy rating attribute', fix: 'Add BEE rating attribute for Amazon.in', severity: 'high' },
  { asin: 'B0CX2345KL', title: 'Yoga Mat Premium 6mm', reason: 'Main image non-compliant background', fix: 'Regenerate hero with white background', severity: 'medium' },
  { asin: 'B0CX5678CD', title: 'Ultra Slim Laptop Stand', reason: 'Variation theme mismatch', fix: 'Update variation theme to COLOR/SIZE', severity: 'medium' },
];

export const DEMO_REVIEW_QUEUE: ReviewItem[] = [
  { id: 'rev-1', type: 'Content Review', title: 'Earbuds Pro title & bullets update', status: 'pending', assignee: 'Listing Team' },
  { id: 'rev-2', type: 'Creative Review', title: 'Hero image set v3 — 4 variants', status: 'in_review', assignee: 'Creative Director' },
  { id: 'rev-3', type: 'SEO Review', title: 'Backend keyword optimization batch', status: 'approved', assignee: 'SEO Lead' },
  { id: 'rev-4', type: 'Compliance Review', title: 'Q3 release package REL-2026-000142', status: 'pending', assignee: 'Compliance Officer' },
];

export const DEMO_ADVISOR_INSIGHTS = [
  'Publish REL-2026-000142 Tuesday 6 AM IST — peak conversion window for electronics',
  'Delay Yoga Mat release — fix hero image compliance before publishing',
  'Improve SEO: add 2 backend keywords to Earbuds Pro before next release',
  'Launch advertising sync with REL-2026-000141 — 23 new listings ready',
  'Inventory check: Smart Home Hub stock below threshold — delay publishing',
];

export function buildCatalogOpsDashboard() {
  return {
    explorer_features: EXPLORER_FEATURES.length,
    lifecycle_stages: LIFECYCLE_STAGES.length,
    release_contents: RELEASE_CONTENTS.length,
    version_features: VERSION_FEATURES.length,
    review_types: REVIEW_TYPES.length,
    simulation_metrics: SIMULATION_METRICS.length,
    validation_checks: VALIDATION_CHECKS.length,
    advisor_recommendations: ADVISOR_RECOMMENDATIONS.length,
  };
}
